import { useCallback, useState, type ReactNode } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Link, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { SectionHeading } from "./ChecklistRow";
import { HelpMark } from "./EvidenceHelpDialog";
import SubmissionAnswerRow from "./SubmissionAnswerRow";
import { FILE_SWATCHES } from "./packageFiles";
import { formatFileSize } from "../../utils/formatFileSize";
import { storefrontSetupHref } from "../../utils/storefrontLinks";
import { ucFirstWord } from "../../utils/textUtils";
import { PAYPAL_NOTE_KEY, STRIPE_FILE_FIELD_LABELS, STRIPE_FIXED_LABELS, type AdyenDefenseReason, type EvidenceDraftSummary, type EvidenceOutline, type EvidenceSubmissionView, type SubmissionPlanFile, type StripeFixedField, type SubmissionProvider } from "../../types/evidence";

dayjs.extend(utc);

/** Mirrors MAX_PAYPAL_NOTE_CHARS in apps/api/modules/evidence/types/evidence.ts. */
const MAX_PAYPAL_NOTE_CHARS = 2000;
/** Mirrors MAX_STRIPE_ANSWER_CHARS in apps/api/modules/evidence/types/evidence.ts. */
const MAX_STRIPE_ANSWER_CHARS = 20000;

// What differs between the processors: Shopify stages the response and sends it on the due date, PayPal takes it at once.
const COPY: Record<SubmissionProvider, {
  name: string; action: string; confirm: string; perFile: string; total: string | null; answersHeading: string; notSent: string; empty: string;
  noPermission: ReactNode; sent: (when: string, who: string | null) => string; progress: (files: number) => string;
}> = {
  shopify: {
    name: "Shopify", action: "Save", confirm: "Save", perFile: "2 MB", total: "4 MB", answersHeading: "Written answers", notSent: "Not saved",
    empty: "Generate the evidence to see what goes to Shopify.",
    noPermission: "This store has not granted the dispute evidence scopes, so only download is available. Reinstall the Chargemont app on the store to submit from here.",
    sent: (when, who) => `Submitted ${when} by ${who}. Shopify accepts one submission per dispute; the case is now under review.`,
    progress: (files) => `Saving ${files} files and the answers…`,
  },
  paypal: {
    name: "PayPal", action: "Submit", confirm: "Submit", perFile: "10 MB", total: "50 MB", answersHeading: "Note to PayPal", notSent: "Not submitted",
    empty: "Generate the evidence to see what goes to PayPal.",
    noPermission: "This PayPal app lacks the Disputes update permission, so only download is available. Turn on Disputes for the app at developer.paypal.com and run a sync.",
    sent: (when, who) => `Submitted ${when} by ${who}. PayPal has the case under review. If PayPal asks again, Submit comes back here.`,
    progress: (files) => `Sending ${files} files and the notes to PayPal…`,
  },
  adyen: {
    name: "Adyen", action: "Submit", confirm: "Submit", perFile: "2 MB", total: null, answersHeading: "Defence reason", notSent: "Not defended",
    empty: "Generate the evidence to see what goes to Adyen.",
    noPermission: "This Adyen credential lacks the API dispute management role, so only download is available. Add the role to the credential in Customer Area (Developers → API credentials → Roles); the portal re-checks when you open this page.",
    sent: (when, who) => `Defended ${when} by ${who}. Adyen sent the defence to the issuer; the outcome arrives with the next Adyen event.`,
    progress: (files) => `Uploading ${files} ${files === 1 ? "document" : "documents"} and defending…`,
  },
  stripe: {
    name: "Stripe", action: "Submit", confirm: "Submit", perFile: "none", total: "4.5 MB · 50 pages", answersHeading: "Stripe's answers", notSent: "Not submitted",
    empty: "Generate the evidence to see what goes to Stripe.",
    noPermission: "This Stripe key cannot submit evidence; upload in the Stripe Dashboard. The key needs Disputes: write and Files: write.",
    sent: (when, who) => `Submitted ${when} by ${who}. Stripe sent the evidence to the issuer; the outcome arrives with the next Stripe event.`,
    progress: (files) => `Uploading ${files} files and submitting…`,
  },
};

type ColumnAlert = { severity: "success" | "warning" | "error" | "info"; text: ReactNode };

function SettingsLink({ href, children }: { href: string | null; children: string }) {
  return href ? <Link href={href} target="_blank" rel="noopener" underline="hover">{children}</Link> : children;
}

function alertFor({ provider, draft, canSubmit, documentStale, skipped, noteEmpty, noteTooLong, overLimit, adyen, stripe, returnAddress, submitError, settingsLink, evidenceDueBy }: {
  provider: SubmissionProvider;
  draft: EvidenceDraftSummary;
  canSubmit: boolean;
  evidenceDueBy: string | null;
  documentStale: boolean;
  skipped: SubmissionPlanFile | undefined;
  noteEmpty: boolean;
  noteTooLong: boolean;
  overLimit: string | null;
  adyen: { reasonsError: string | null; noReason: boolean; dueEnded: string | null } | null;
  stripe: { dueEnded: string | null; notAccepting: boolean; tooLong: string | null } | null;
  /** Null unless PayPal asks for a return address. */
  returnAddress: { saved: boolean; otherAsks: string[]; brand: string; brandHref: string | null } | null;
  submitError: string | null;
  settingsLink: boolean;
}): ColumnAlert | null {
  const copy = COPY[provider];
  const last = draft.submission;
  if (last?.status === "sent" && !draft.paypal?.reopened) return { severity: "success", text: copy.sent(dayjs(last.sentAt).format("MMM D, HH:mm"), last.userName) };
  // A row still sending: the next try after 15 minutes settles it against the processor's record and sends only if nothing landed.
  if (last?.status === "sending") {
    return isSettling(last)
      ? { severity: "info", text: `An attempt from ${dayjs(last.updatedAt).format("MMM D, HH:mm")} is still being checked with ${copy.name}; try again in 15 minutes.` }
      : { severity: "warning", text: `An attempt from ${dayjs(last.updatedAt).format("MMM D, HH:mm")} was never confirmed. ${copy.action} checks ${copy.name}'s record first and sends again only if nothing landed.` };
  }
  if (!canSubmit) {
    return {
      severity: "warning",
      text: (
        <>
          {copy.noPermission}
          {settingsLink && <> <Link href="/companies?tab=accounts" target="_blank" rel="noopener" underline="hover">Payment processors</Link></>}
        </>
      ),
    };
  }
  if (documentStale) return { severity: "warning", text: "Update evidence to submit the latest changes." };
  if (overLimit) return { severity: "warning", text: overLimit };
  if (skipped?.skipReason === `over ${copy.perFile}`) {
    return { severity: "warning", text: `${skipped.label} is ${formatFileSize(skipped.bytes)}, over ${copy.name}'s ${copy.perFile} per file. Remove a screenshot${skipped.file === "uncategorized" ? "" : " in that section"} or regenerate before submitting.` };
  }
  if (skipped) return { severity: "warning", text: `${skipped.label}: ${skipped.skipReason}.` };
  if (noteEmpty) return { severity: "warning", text: "Write the note to PayPal before submitting." };
  if (noteTooLong) return { severity: "warning", text: `The note is over ${MAX_PAYPAL_NOTE_CHARS.toLocaleString("en-US")} characters; shorten it.` };
  if (adyen?.reasonsError) return { severity: "error", text: adyen.reasonsError };
  if (adyen?.dueEnded) return { severity: "warning", text: `The defence period ended ${adyen.dueEnded}.` };
  if (adyen?.noReason) return { severity: "warning", text: "No defence reason Adyen allows can be filled from this document; defend in Customer Area." };
  if (stripe?.dueEnded) return { severity: "warning", text: `The response window ended ${stripe.dueEnded}.` };
  if (stripe?.notAccepting) return { severity: "warning", text: "Stripe no longer takes evidence on this dispute." };
  if (stripe?.tooLong) return { severity: "warning", text: stripe.tooLong };
  if (submitError) return { severity: "error", text: submitError };
  if (last?.status === "saved" && last.stale) return { severity: "warning", text: "Changed since the save. Save again so Shopify sends the current version." };
  if (last?.status === "saved") {
    const due = evidenceDueBy ? ` on ${dayjs.utc(evidenceDueBy).format("MMM D, HH:mm [UTC]")}` : " on the due date";
    return { severity: "success", text: `Saved in Shopify by ${last.userName}. Shopify sends it${due}. To send it earlier, open the dispute in Shopify, pick the reason and press Submit now.` };
  }
  if (last?.status === "failed") return { severity: "error", text: `${copy.name} refused the last attempt: ${last.errorMessage?.replace(/^(Shopify|PayPal|Adyen|Stripe) refused the (submission|defence): /, "") ?? "no message"}` };
  const reopened = draft.paypal?.reopened ?? null;
  const stale = reopened && draft.paypal?.stale_pack ? ` This pack was generated ${dayjs(draft.generated_at).format("MMM D")}, before the reopen.` : "";
  if (returnAddress && !returnAddress.saved) {
    const also = returnAddress.otherAsks.length ? `, and for ${returnAddress.otherAsks.join(", ")}` : "";
    return {
      severity: "warning",
      text: <>{`PayPal asks for a return address so the buyer can ship the item back${also}.${stale} None is saved for ${returnAddress.brand}: add it under `}<SettingsLink href={returnAddress.brandHref}>Brand settings</SettingsLink>, or submit without one.</>,
    };
  }
  // A saved return address goes with the response on its own: only a pack older than the reopen still needs a word.
  if (reopened && (!returnAddress || stale)) return { severity: "warning", text: `PayPal asks for ${reopened.requested.join(", ")}.${stale} Regenerate the evidence first, or submit it as is.` };
  return null;
}

/** Mirrors SENDING_TIMEOUT_MINUTES in apps/api/modules/evidence/submit/submissionRecord.ts. */
const SENDING_TIMEOUT_MINUTES = 15;
const isSettling = (last: NonNullable<EvidenceDraftSummary["submission"]>) => dayjs().diff(dayjs(last.updatedAt), "minute") < SENDING_TIMEOUT_MINUTES;

function statusLine(provider: SubmissionProvider, last: EvidenceDraftSummary["submission"], rounds: number): string {
  if (last?.status === "sent" && last.sentAt) {
    const round = rounds > 1 ? ` · ${toOrdinal(rounds)} round` : "";
    return `Submitted ${dayjs(last.sentAt).format("MMM D, HH:mm")}${round}`;
  }
  if (last?.status === "saved") return `Saved ${dayjs(last.updatedAt).format("MMM D, HH:mm")}`;
  if (last?.status === "sending") return "Sending";
  return COPY[provider].notSent;
}

function toOrdinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th";
  return `${n}${suffix}`;
}

function FileRows({ files, captions }: { files: SubmissionPlanFile[]; captions?: Partial<Record<string, string>> }) {
  return files.map((f) => (
    <Box key={`${f.file}-${f.label}`} sx={{ display: "grid", gridTemplateColumns: "12px 1fr auto", columnGap: "12px", alignItems: "center", py: 1, borderTop: 1, borderColor: "divider", opacity: f.send ? 1 : 0.55 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: FILE_SWATCHES[f.file] }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{f.label}</Typography>
        {f.send && captions?.[f.file] && (
          <Typography variant="caption" color="text.secondary" display="block">{captions[f.file]}</Typography>
        )}
        {!f.send && (
          <Typography variant="caption" color="text.secondary" display="block">
            {`not sent: ${f.skipReason}`}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        {`${formatFileSize(f.bytes)} · ${f.pages} ${f.pages === 1 ? "page" : "pages"}`}
      </Typography>
    </Box>
  ));
}

function FixedRow({ label, source, children }: { label: string; source: string; children: ReactNode }) {
  return (
    <Box sx={{ py: 1, borderTop: 1, borderColor: "divider" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto !important", whiteSpace: "nowrap" }}>{`from ${source}`}</Typography>
      </Stack>
      {children}
    </Box>
  );
}

export default function SubmissionColumn({ provider, draft, canSubmit, evidenceDueBy, ppStatus, submission, submissionError, submitError, documentStale, inProgress, submitting, settingsLink, storefront, onSaveAnswer, onSubmit, onHelp }: {
  provider: SubmissionProvider;
  draft: EvidenceDraftSummary;
  canSubmit: boolean;
  evidenceDueBy: string | null;
  ppStatus: string | null;
  submission: EvidenceSubmissionView | null;
  submissionError: string | null;
  submitError: string | null;
  documentStale: boolean;
  inProgress: boolean;
  submitting: boolean;
  settingsLink: boolean;
  /** The outline's storefront: the brand the pack printed, whose return address goes to PayPal. */
  storefront: EvidenceOutline["storefront"] | null;
  onSaveAnswer: (field: string, text: string | null) => Promise<boolean>;
  onSubmit: (reasonCode?: string) => Promise<boolean>;
  onHelp: () => void;
}) {
  const copy = COPY[provider];
  const [confirming, setConfirming] = useState(false);
  const [editingFields, setEditingFields] = useState<string[]>([]);
  const onEditingChange = useCallback((field: string, editing: boolean) => {
    setEditingFields((fields) => {
      if (editing) return fields.includes(field) ? fields : [...fields, field];
      return fields.includes(field) ? fields.filter((f) => f !== field) : fields;
    });
  }, []);

  const reopened = draft.paypal?.reopened ?? null;
  const sent = draft.submission?.status === "sent" && !reopened;
  const action = reopened ? "Submit again" : copy.action;
  const answers = submission ? Object.entries(submission.answers).filter((entry): entry is [string, NonNullable<typeof entry[1]>] => Boolean(entry[1])) : [];
  const paypal = submission?.provider === "paypal" ? submission : null;
  const note = paypal?.answers[PAYPAL_NOTE_KEY]?.text ?? "";
  const noteEmpty = Boolean(paypal) && !note.trim();
  const noteTooLong = Boolean(paypal) && note.length > MAX_PAYPAL_NOTE_CHARS;
  const adyen = submission?.provider === "adyen" ? submission : null;
  const stripe = submission?.provider === "stripe" ? submission : null;
  // Adyen's reasons only map sendable files, so a skipped one holds nothing; adyenState holds the column instead.
  const skipped = adyen ? undefined : submission?.files.find((f) => !f.send);
  const reason = adyen?.reasons.find((r) => r.code === adyen.selected) ?? null;
  const documents = (reason?.documents ?? []).flatMap((d) => {
    if (!d.file) return [];
    const f = submission?.files.find((x) => x.file === d.file);
    return [{ type: d.type, typeLabel: d.label, file: d.file, label: f?.label ?? d.file, bytes: f?.bytes ?? 0, pages: f?.pages ?? 0, send: true, skipReason: null }];
  });
  const documentBytes = [...new Map(documents.map((d) => [d.file, d.bytes])).values()].reduce((sum, bytes) => sum + bytes, 0);
  // Each file's Adyen document type(s) under the selected reason; a file the reason does not use says so.
  const adyenCaptions = adyen ? Object.fromEntries(adyen.files.map((f) => {
    const types = documents.filter((d) => d.file === f.file).map((d) => d.typeLabel);
    return [f.file, types.length ? `as ${types.join(", ")}` : "not used by this reason"];
  })) : undefined;
  const reasonFiles = adyen ? adyen.files.filter((f) => documents.some((d) => d.file === f.file)) : [];
  const dueEnded = (adyen || stripe) && evidenceDueBy && dayjs.utc(evidenceDueBy).isBefore(dayjs.utc()) ? dayjs.utc(evidenceDueBy).format("MMM D, YYYY") : null;
  const adyenState = adyen ? { reasonsError: adyen.reasonsError, noReason: !reason, dueEnded } : null;
  const overLimit = stripe ? stripe.overLimit : draft.package?.total.overLimit ?? null;
  const longAnswer = stripe ? answers.find(([, a]) => a.text.length > MAX_STRIPE_ANSWER_CHARS) : undefined;
  const stripeState = stripe ? {
    dueEnded,
    notAccepting: ppStatus !== null && ppStatus !== "needs_response",
    tooLong: longAnswer ? `${longAnswer[1].label} is over ${MAX_STRIPE_ANSWER_CHARS.toLocaleString("en-US")} characters; shorten it.` : null,
  } : null;
  const asked = paypal?.returnAddress.requested ? paypal : null;
  const saved = asked?.returnAddress.address;
  const addressLine = saved ? [saved.address_line_1, saved.admin_area_2, saved.admin_area_1, saved.postal_code, saved.country_code].join(", ") : null;
  const brandHref = settingsLink && storefront?.companyId != null ? storefrontSetupHref({ companyId: storefront.companyId, storefrontId: storefront.printedId }) : null;
  const returnAddress = asked && {
    saved: addressLine !== null,
    otherAsks: asked.requested.filter((r) => r.type !== "RETURN_ADDRESS_FOR_SHIPPING").map((r) => r.label),
    brand: storefront?.printedName ?? "this brand",
    brandHref,
  };
  const alert = alertFor({ provider, draft, canSubmit, documentStale, skipped, noteEmpty, noteTooLong, overLimit, adyen: adyenState, stripe: stripeState, returnAddress, submitError, settingsLink, evidenceDueBy });
  const pending = draft.submission?.status === "sending" && isSettling(draft.submission);
  const files = submission ? submission.files.filter((f) => f.send).length : 0;
  const disabled = !submission || !canSubmit || pending || documentStale || Boolean(overLimit) || Boolean(skipped) || noteEmpty || noteTooLong || inProgress || submitting || editingFields.length > 0
    || Boolean(adyenState && (adyenState.reasonsError || adyenState.noReason || adyenState.dueEnded))
    || Boolean(stripeState && (stripeState.dueEnded || stripeState.notAccepting || stripeState.tooLong)) || Boolean(stripe && files === 0);
  // Each file's Stripe evidence field, shown under its name in the list and the dialog.
  const captions = adyenCaptions ?? (stripe ? Object.fromEntries(stripe.files.map((f) => [f.file, `as ${STRIPE_FILE_FIELD_LABELS[f.file as keyof typeof STRIPE_FILE_FIELD_LABELS]}`])) : undefined);
  const sendingAnswers = stripe ? answers.filter(([, a]) => a.text.trim()).length : 0;
  const pages = stripe ? stripe.totalPages + stripe.textPages : 0;
  const total = stripe
    ? `${formatFileSize(stripe.totalBytes)} · ${pages} ${pages === 1 ? "page" : "pages"}${stripe.textPages ? ` (${stripe.textPages} from answers)` : ""}`
    : submission ? `${formatFileSize(submission.totalBytes)}${copy.total ? ` of ${copy.total}` : ""}` : "";
  const sending = adyen ? documents.length : files;
  const edited = answers.filter(([, a]) => a.edited).length;
  const fixed = submission?.provider === "shopify" ? submission.fixed : null;
  const address = fixed?.shippingAddress;

  const send = async () => {
    await onSubmit(reason?.code);
    setConfirming(false);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Mirrors the Actions card across the preview: two meta lines, the button, its warning, then the lists scroll below. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Typography variant="body2" color="text.secondary" component="div" sx={{ flexGrow: 1 }}>
          {reopened ? (
            <>
              <Box sx={{ color: "warning.dark" }}>{`Reopened ${dayjs(reopened.at).format("MMM D")}${evidenceDueBy ? ` · due ${dayjs.utc(evidenceDueBy).format("MMM D")}` : ""}`}</Box>
              {reopened.reason_changed && reopened.reason && <div>{`Reason changed to ${ucFirstWord(reopened.reason)}`}</div>}
            </>
          ) : <div>{statusLine(provider, draft.submission, draft.paypal?.rounds ?? 0)}</div>}
          <div>{submission ? `${files} ${files === 1 ? "file" : "files"} · ${total}` : "No files yet"}</div>
        </Typography>
        <HelpMark label="How submitting works" onClick={onHelp} />
      </Box>
      {!sent && (
        <Button variant="contained" fullWidth sx={{ mt: 1.5 }} disabled={disabled} onClick={() => setConfirming(true)}>{action}</Button>
      )}
      {alert && <Alert severity={alert.severity} sx={{ mt: 1.5 }}>{alert.text}</Alert>}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>

      {!submission ? (
        submissionError
          ? <Alert severity="error" sx={{ mt: 1 }}>{submissionError}</Alert>
          : <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{copy.empty}</Typography>
      ) : (
        <>
          <Box sx={{ mt: 2 }}>
            <SectionHeading>Files</SectionHeading>
            <FileRows files={submission.files} captions={captions} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <SectionHeading>{copy.answersHeading}</SectionHeading>
            {paypal && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {`PayPal asked for: ${paypal.requested.map((r) => r.label).join(", ")}.`}
              </Typography>
            )}
            {adyen && (
              <>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {adyen.reasonsError ? "Could not load Adyen's reasons." : `Adyen allows ${adyen.reasons.length} ${adyen.reasons.length === 1 ? "reason" : "reasons"} for this chargeback.`}
                </Typography>
                {adyen.reasons.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel id="defence-reason-label">Defence reason</InputLabel>
                    <Select
                      labelId="defence-reason-label"
                      label="Defence reason"
                      value={adyen.selected ?? ""}
                      disabled={sent || inProgress || submitting}
                      onChange={(e) => { void onSaveAnswer("reasonCode", String(e.target.value)); }}
                    >
                      {adyen.reasons.map((r: AdyenDefenseReason) => (
                        <MenuItem key={r.code} value={r.code} disabled={!r.satisfiable}>{`${r.label}${r.satisfiable ? "" : ` · needs ${r.missing.join(", ")}`}`}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {!adyen && answers.map(([f, answer]) => (
              <SubmissionAnswerRow key={f} field={f} answer={answer} disabled={sent || inProgress || submitting} onSave={(text) => onSaveAnswer(f, text)} onEditingChange={onEditingChange} />
            ))}
            {stripe && Object.keys(stripe.fixed).length > 0 && (
              <FixedRow label="Sent automatically" source="Order">
                {(Object.entries(stripe.fixed) as [StripeFixedField, string][]).map(([field, value]) => (
                  <Typography key={field} variant="body2">{`${STRIPE_FIXED_LABELS[field]}: ${value}`}</Typography>
                ))}
              </FixedRow>
            )}
            {fixed && (
              <FixedRow label="Customer and shipping address" source="Order">
                <Typography variant="body2">{[[fixed.customerFirstName, fixed.customerLastName].filter(Boolean).join(" "), fixed.customerEmail].filter(Boolean).join(" · ")}</Typography>
                {address && <Typography variant="body2">{[address.address1, address.address2, address.city, address.province, address.zip, address.country].filter(Boolean).join(", ")}</Typography>}
              </FixedRow>
            )}
            {paypal && paypal.tracking.length > 0 && (
              <FixedRow label="Tracking" source="Order">
                {paypal.tracking.map((t) => <Typography key={t.number} variant="body2">{[t.carrier, t.number].filter(Boolean).join(" · ")}</Typography>)}
              </FixedRow>
            )}
            {returnAddress && (
              <FixedRow label="Return address" source="Brand">
                {addressLine
                  ? <Typography variant="body2">{addressLine}</Typography>
                  : <Typography variant="body2" color="text.secondary">None saved. <SettingsLink href={brandHref}>Add it on the brand.</SettingsLink></Typography>}
              </FixedRow>
            )}
          </Box>
        </>
      )}
      </Box>

      {submission && (
        <Dialog open={confirming} onClose={(_, reason) => { if (reason !== "backdropClick" && !submitting) setConfirming(false); }} fullWidth maxWidth="sm">
          <DialogTitle>{action}</DialogTitle>
          <DialogContent>
            {adyen
              ? <FileRows files={reasonFiles} captions={adyenCaptions} />
              : <FileRows files={submission.files} captions={captions} />}
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {adyen
                ? `${documents.length} ${documents.length === 1 ? "document" : "documents"} · ${formatFileSize(documentBytes)}.`
                : stripe
                  ? `${total} of ${copy.total}. ${sendingAnswers} ${sendingAnswers === 1 ? "answer goes" : "answers go"} as text.`
                  : `${total}. Plus ${provider === "paypal" ? "the note" : "Shopify's answers"}${edited ? `, ${edited} edited` : ""}.`}
            </Typography>
            {reason && <Typography variant="body2" sx={{ mt: 1 }}>{`Defended as ${reason.label}.`}</Typography>}
            {paypal && <Typography variant="body2" sx={{ mt: 1 }}>{`Sent as ${paypal.entry.label}${paypal.entry.type === "PROOF_OF_FULFILLMENT" ? ", with the tracking" : ""}.`}</Typography>}
            {provider === "shopify"
              ? <Alert severity="info" sx={{ mt: 1.5 }}>Shopify keeps it editable until the due date and sends it then. To send it earlier, press Submit now in Shopify.</Alert>
              : provider === "paypal"
                ? <Alert severity="warning" sx={{ mt: 1.5 }}>PayPal moves the case to under review and may ask again; Submit comes back here if it does.</Alert>
                : provider === "stripe"
                  ? <Alert severity="warning" sx={{ mt: 1.5 }}>Stripe submits the evidence to the issuer at once; nothing can be added after.</Alert>
                  : <Alert severity="warning" sx={{ mt: 1.5 }}>Adyen sends the defence to the issuer at once; nothing can be added after.</Alert>}
            {submitting && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: "center" }}>
                <CircularProgress size={16} />
                <Typography variant="body2">{copy.progress(sending)}</Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(false)} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={() => void send()} disabled={submitting}>{copy.confirm}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
}
